import { Injectable, Injector, signal, inject } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  DEFAULT_TOAST_DURATIONS,
  MAX_TOASTS,
  ShowToastOptions,
  Toast,
} from './toast.model';
import { ToastContainerComponent } from './toast-container.component';

@Injectable({ providedIn: 'root' })
export class ToastProvider {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  readonly toasts = signal<Toast[]>([]);

  private overlayRef: OverlayRef | null = null;
  private nextId = 0;
  private dismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  show(options: ShowToastOptions): string {
    this.ensureOverlayAttached();

    const toast: Toast = {
      id: this.createId(),
      message: options.message,
      variant: options.variant,
      duration: options.duration ?? DEFAULT_TOAST_DURATIONS[options.variant],
      action: options.action,
      dismissible: options.dismissible ?? true,
    };

    this.toasts.update(current => [toast, ...current].slice(0, MAX_TOASTS));

    if (toast.duration > 0) {
      const timeoutId = setTimeout(() => this.dismiss(toast.id), toast.duration);
      this.dismissTimeouts.set(toast.id, timeoutId);
    }

    return toast.id;
  }

  dismiss(id: string): void {
    const timeoutId = this.dismissTimeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.dismissTimeouts.delete(id);
    }

    this.toasts.update(current => current.filter(toast => toast.id !== id));

    if (this.toasts().length === 0) {
      this.disposeOverlay();
    }
  }

  dismissAll(): void {
    for (const timeoutId of this.dismissTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.dismissTimeouts.clear();
    this.toasts.set([]);
    this.disposeOverlay();
  }

  private createId(): string {
    this.nextId += 1;
    return `toast-${this.nextId}`;
  }

  private ensureOverlayAttached(): void {
    if (this.overlayRef) {
      return;
    }

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .global()
        .top('60px')
        .centerHorizontally(),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      panelClass: 'toast-overlay-panel',
    });

    this.overlayRef.attach(
      new ComponentPortal(ToastContainerComponent, null, this.injector),
    );
  }

  private disposeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
